import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Loader2, ShoppingCart } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import { trpc } from '@/lib/trpc';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useLocations } from '@/features/locations/hooks/useLocations';
import { useSuppliers } from '@/features/suppliers/hooks/useSuppliers';
import { useAddSelectionToPurchaseOrder } from '../utils/pipeline-procedures';

const APPENDABLE = ['draft', 'submitted', 'in_production', 'ready', 'partially_received'];
const CURRENCIES = ['AUD', 'USD', 'CNY', 'EUR', 'GBP'];

export interface SelectionOrderRecord {
  id: string;
  styleName: string;
  styleCode: string | null;
  supplierId: string | null;
  supplierTitle: string | null;
  agreedPrice: number | null;
  agreedCurrency: string | null;
  proposedQuantity: number | null;
  moq: number | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  records: SelectionOrderRecord[];
  onComplete: () => void;
}

type RowReview = { price: string; currency: string; fallbackQuantity: string };

export function SelectionOrderDialog({ open, onOpenChange, records, onComplete }: Props) {
  const { t } = useTranslation('productDev');
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { locations } = useLocations();
  const { suppliers } = useSuppliers();
  const orgCurrency = organization?.default_currency ?? 'USD';
  const mutation = useAddSelectionToPurchaseOrder();
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [locationId, setLocationId] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [poId, setPoId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [etaDate, setEtaDate] = useState('');
  const [rows, setRows] = useState<Record<string, RowReview>>({});
  const recordKey = records.map((record) => record.id).join(',');

  useEffect(() => {
    if (!open) return;
    const first = records[0];
    setTitle(records.length === 1
      ? first?.styleName ?? ''
      : first ? t('selectionOrder.defaultTitle', { name: first.styleName, count: records.length - 1 }) : '');
    const supplierCounts = new Map<string, number>();
    for (const record of records) {
      if (record.supplierId) supplierCounts.set(record.supplierId, (supplierCounts.get(record.supplierId) ?? 0) + 1);
    }
    const commonSupplier = [...supplierCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
    setSupplierId(commonSupplier ?? '');
    setRows(Object.fromEntries(records.map((record) => [record.id, {
      price: record.agreedPrice == null ? '' : String(record.agreedPrice),
      currency: record.agreedCurrency ?? orgCurrency,
      fallbackQuantity: String(record.proposedQuantity ?? record.moq ?? 1),
    }])));
  }, [open, recordKey, orgCurrency, records, t]);

  const ordersQuery = trpc.purchaseOrders.getOrders.useQuery({}, {
    enabled: open && mode === 'existing', staleTime: 60_000, refetchOnWindowFocus: false,
  });
  const poOptions = useMemo(() => (ordersQuery.data?.data ?? []).filter((po) => APPENDABLE.includes(po.status)), [ordersQuery.data]);
  const mixedSuppliers = new Set(records.map((record) => record.supplierId ?? 'none')).size > 1;
  const validRows = records.every((record) => {
    const row = rows[record.id];
    const price = Number(row?.price);
    const quantity = Number(row?.fallbackQuantity);
    return row?.price.trim() !== '' && Number.isFinite(price) && price >= 0 &&
      Number.isInteger(quantity) && quantity > 0;
  });
  const canSubmit = validRows && (mode === 'new' ? Boolean(locationId && supplierId && title.trim()) : Boolean(poId));

  const patchRow = (id: string, patch: Partial<RowReview>) => setRows((current) => ({
    ...current,
    [id]: { price: '', currency: orgCurrency, fallbackQuantity: '1', ...current[id], ...patch },
  }));

  const submit = async () => {
    if (!canSubmit || mutation.isPending) return;
    try {
      const result = await mutation.mutateAsync({
        items: records.map((record) => ({
          productDevId: record.id,
          unitPrice: Number(rows[record.id]!.price),
          currency: rows[record.id]!.currency,
          fallbackQuantity: Number(rows[record.id]!.fallbackQuantity),
        })),
        target: mode === 'new'
          ? { mode: 'new', locationId, supplierId, title: title.trim(), description: description.trim() || null, etaDate: etaDate || null }
          : { mode: 'existing', poId },
      });
      toast({
        title: t('selectionOrder.toast.added'),
        description: t('selectionOrder.toast.summary', {
          records: result.recordCount,
          lines: result.lineCount,
          matched: result.matchedSkuCount,
        }),
      });
      onOpenChange(false);
      onComplete();
      navigate(`/orders/${result.poId}`);
    } catch (error) {
      toast({
        title: t('selectionOrder.toast.failed'),
        description: error instanceof Error ? error.message : t('addToPo.toast.tryAgain'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !mutation.isPending && onOpenChange(next)}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{t('selectionOrder.title', { count: records.length })}</DialogTitle>
          <DialogDescription>{t('selectionOrder.description')}</DialogDescription>
        </DialogHeader>

        <RadioGroup value={mode} onValueChange={(value) => setMode(value as 'new' | 'existing')} className="grid gap-3 sm:grid-cols-2">
          <Label className="flex cursor-pointer items-center gap-3 rounded-md border p-3"><RadioGroupItem value="new" />{t('addToPo.newPo')}</Label>
          <Label className="flex cursor-pointer items-center gap-3 rounded-md border p-3"><RadioGroupItem value="existing" />{t('addToPo.existingPo')}</Label>
        </RadioGroup>

        {mode === 'new' ? (
          <div className="grid gap-4 rounded-lg border p-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label htmlFor="selection-po-title">{t('selectionOrder.orderTitle')}</Label><Input id="selection-po-title" value={title} onChange={(e) => setTitle(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>{t('addToPo.supplier')}</Label><Select value={supplierId} onValueChange={setSupplierId}><SelectTrigger><SelectValue placeholder={t('selectionOrder.chooseSupplier')} /></SelectTrigger><SelectContent>{suppliers.map((supplier) => <SelectItem key={supplier.id} value={supplier.id}>{supplier.title}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>{t('addToPo.location')}</Label><Select value={locationId} onValueChange={setLocationId}><SelectTrigger><SelectValue placeholder={t('addToPo.chooseLocation')} /></SelectTrigger><SelectContent>{locations.map((location) => <SelectItem key={location.id} value={location.id}>{location.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label htmlFor="selection-po-eta">{t('selectionOrder.eta')}</Label><Input id="selection-po-eta" type="date" value={etaDate} onChange={(e) => setEtaDate(e.target.value)} /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label htmlFor="selection-po-description">{t('selectionOrder.orderDescription')}</Label><Textarea id="selection-po-description" value={description} onChange={(e) => setDescription(e.target.value)} /></div>
          </div>
        ) : (
          <div className="space-y-1.5"><Label>{t('addToPo.purchaseOrder')}</Label><Select value={poId} onValueChange={setPoId}><SelectTrigger><SelectValue placeholder={t('addToPo.choosePo')} /></SelectTrigger><SelectContent>{poOptions.map((po) => <SelectItem key={po.id} value={po.id}>{po.po_number ?? po.title ?? po.id.slice(0, 8)}</SelectItem>)}</SelectContent></Select></div>
        )}

        {mixedSuppliers && mode === 'new' && <Alert><AlertTriangle className="h-4 w-4" /><AlertDescription>{t('selectionOrder.mixedSuppliers')}</AlertDescription></Alert>}

        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground"><tr><th className="p-3">{t('list.col.style')}</th><th className="p-3">{t('selectionOrder.fallbackQuantity')}</th><th className="p-3">{t('addToPo.unitPrice')}</th><th className="p-3">{t('commercials.currency')}</th></tr></thead>
            <tbody>{records.map((record) => <tr key={record.id} className="border-t"><td className="p-3 font-medium">{record.styleCode ? `${record.styleCode}: ` : ''}{record.styleName}</td><td className="p-3"><Input className="w-24" type="number" min={1} step={1} value={rows[record.id]?.fallbackQuantity ?? ''} onChange={(e) => patchRow(record.id, { fallbackQuantity: e.target.value })} /></td><td className="p-3"><Input className="w-28" type="number" min={0} step="0.01" value={rows[record.id]?.price ?? ''} onChange={(e) => patchRow(record.id, { price: e.target.value })} /></td><td className="p-3"><Select value={rows[record.id]?.currency ?? orgCurrency} onValueChange={(currency) => patchRow(record.id, { currency })}><SelectTrigger className="w-24"><SelectValue /></SelectTrigger><SelectContent>{CURRENCIES.map((currency) => <SelectItem key={currency} value={currency}>{currency}</SelectItem>)}</SelectContent></Select></td></tr>)}</tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground">{t('selectionOrder.quantityHint')}</p>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>{t('addToPo.cancel')}</Button>
          <Button onClick={() => void submit()} disabled={!canSubmit || mutation.isPending}>{mutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShoppingCart className="mr-2 h-4 w-4" />}{t('selectionOrder.create')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
